import InfoIcon from "@assets/images/info.svg";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import { SingleSelectField } from "@saleor/components/SingleSelectField";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import { GetStockOnHandReportQueryResult } from "@saleor/graphql";
import { renderCollection } from "@saleor/misc";
import { StockOnHandSortField } from "@saleor/stockOnHand/views/ReportStockOnHandPage/sort";
import { SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import classNames from "classnames";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import useStyles from "./styles";

interface StockOnHandTableResultProps extends SortPage<StockOnHandSortField> {
  dataStockOnHand: GetStockOnHandReportQueryResult["data"]["getStockOnHandReport"];
  filterPlant: string;
  setFilterPlant: (string) => void;
}

export enum TableTab {
  tabA = "tabA",
  tabB = "tabB",
  tabC = "tabC"
}

const tabList = [
  {
    id: "customer-order-tab",
    code: TableTab.tabA,
    title: "Customer Order",
    index: "z3",
    first: true
  },
  {
    id: "consignment-tab",
    code: TableTab.tabB,
    title: "Consignment",
    index: "z2",
    first: false
  },
  {
    id: "free-stock-tab",
    code: TableTab.tabC,
    title: "Free stock",
    index: "z1",
    first: false
  }
];

const StockOnHandTableResult: React.FC<StockOnHandTableResultProps> = props => {
  const { dataStockOnHand, filterPlant, setFilterPlant, sort, onSort } = props;
  const [activeTab, setActiveTab] = useState<TableTab>(TableTab.tabA);
  const [reRender, setReRender] = useState<boolean>(false);

  const classes = useStyles();
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const onTabClick = tab => {
    setActiveTab(tab);
  };

  useEffect(() => {
    setReRender(prevState => !prevState);
    if (containerRef.current) {
      containerRef!.current!.scrollLeft = 0;
    }
  }, [activeTab]);

  const plantFilterChoices = useMemo(
    () => [
      { label: "All", value: "All" },
      ...dataStockOnHand.allPlant.map(plant => ({
        label: plant,
        value: plant
      }))
    ],
    [dataStockOnHand]
  );

  const handleChangePlantFilter = choice => {
    setFilterPlant(choice.target.value);
  };

  const renderHeaderTabCustomer = () => (
    <>
      <TableCellHeader
        className={classNames(classes.col_1a, classes.freeze_1)}
        direction={
          sort.sort === StockOnHandSortField.customer
            ? getArrowDirection(sort.asc)
            : undefined
        }
        arrowPosition="right"
        data-testid="customer-sort"
        onClick={() => onSort(StockOnHandSortField.customer)}
      >
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Customer" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader className={classes.col_2a}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "SO No. / Item" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader className={classes.col_3a}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "PO No." })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader className={classes.col_4a}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Request Date" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader className={classes.col_5a}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "QTY" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader className={classes.col_6a}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Pending QTY" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader className={classes.col_7a}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Plant" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader className={classes.col_8a}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Create Date" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader className={classes.col_9a}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Create by" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader className={classes.col_10a}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Sale rep." })}
        </MyTypography>
      </TableCellHeader>
    </>
  );

  const renderHeaderTabConsiment = () => (
    <>
      <TableCellHeader
        className={classNames(classes.col_1a, classes.freeze_1)}
        direction={
          sort.sort === StockOnHandSortField.customer
            ? getArrowDirection(sort.asc)
            : undefined
        }
        arrowPosition="right"
        onClick={() => onSort(StockOnHandSortField.customer)}
      >
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Customer" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader className={classes.col_2b}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "QTY" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader className={classes.col_3b}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Future dummy stock" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader className={classes.col_4b}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Plant" })}
        </MyTypography>
      </TableCellHeader>
    </>
  );

  const renderHeaderTabFree = () => (
    <>
      <TableCellHeader className={classes.col_1c}>
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "Plant" })}
        </MyTypography>
      </TableCellHeader>
      <TableCellHeader
        direction={
          sort.sort === StockOnHandSortField.quantity
            ? getArrowDirection(sort.asc)
            : undefined
        }
        arrowPosition="right"
        onClick={() => onSort(StockOnHandSortField.quantity)}
      >
        <MyTypography variant={"subtitle3"}>
          {intl.formatMessage({ defaultMessage: "QTY" })}
        </MyTypography>
      </TableCellHeader>
    </>
  );

  const renderBodyTabCustomer = () =>
    dataStockOnHand?.customerOrderList?.length
      ? renderCollection(dataStockOnHand.customerOrderList, item => (
          <TableRow>
            <TableCell
              className={classNames(
                classes.col_1a,
                classes.freeze_1,
                classes.zIndex1
              )}
            >
              <MyTypography variant={"body3"}>{item.customer}</MyTypography>
            </TableCell>
            <TableCell>
              <MyTypography variant={"body3"}>
                {item.soNoSlashItem}
              </MyTypography>
            </TableCell>
            <TableCell>
              <MyTypography variant={"body3"}>{item.poNo}</MyTypography>
            </TableCell>
            <TableCell>
              <MyTypography variant={"body3"}>
                {item.requestDate ? item?.requestDate : ""}
              </MyTypography>
            </TableCell>
            <TableCell>
              <MyTypography variant={"body3"}>{item.quantity}</MyTypography>
            </TableCell>
            <TableCell>
              <MyTypography variant={"body3"}>
                {item.pendingQuantity}
              </MyTypography>
            </TableCell>
            <TableCell>
              <MyTypography variant={"body3"}>{item.plant}</MyTypography>
            </TableCell>
            <TableCell>
              <MyTypography variant={"body3"}>
                {item.createDate
                  ? moment(item.createDate, "DD/MM/YYYY").format("DD/MM/YYYY")
                  : ""}
              </MyTypography>
            </TableCell>
            <TableCell>
              <MyTypography variant={"body3"}>
                {item.createBy?.firstName} {item.createBy?.lastName}
              </MyTypography>
            </TableCell>
            <TableCell>
              <MyTypography variant={"body3"}>{item.saleRep}</MyTypography>
            </TableCell>
          </TableRow>
        ))
      : renderBoxEmpty(10);

  const renderBodyTabConsiment = () =>
    dataStockOnHand?.consignmentList.length
      ? renderCollection(dataStockOnHand.consignmentList, item => (
          <TableRow>
            <TableCell
              className={classNames(
                classes.col_1a,
                classes.freeze_1,
                classes.zIndex1
              )}
            >
              <MyTypography variant={"body3"}>
                {item.customerSession}
              </MyTypography>
            </TableCell>
            <TableCell>
              <MyTypography variant={"body3"}>{item.quantity}</MyTypography>
            </TableCell>
            <TableCell>
              <MyTypography variant={"body3"}>
                {item.futureDummyStock}
              </MyTypography>
            </TableCell>
            <TableCell>
              <MyTypography variant={"body3"}>{item.plant}</MyTypography>
            </TableCell>
          </TableRow>
        ))
      : renderBoxEmpty(4);

  const renderBodyTabFree = () =>
    dataStockOnHand?.freeStockList?.length
      ? renderCollection(dataStockOnHand.freeStockList, item => (
          <TableRow>
            <TableCell className={classes.col_1c}>
              <MyTypography variant={"body3"}>{item.plant}</MyTypography>
            </TableCell>
            <TableCell>
              <MyTypography variant={"body3"}>{item.quantity}</MyTypography>
            </TableCell>
          </TableRow>
        ))
      : renderBoxEmpty(2);

  const renderBoxEmpty = (column = 0) =>
    column ? (
      <TableRow>
        <TableCell colSpan={column}>
          <div className={classNames(classes.emptyBox, classes.noBorder)}>
            <div className={classes.infoEmpty}>
              <img
                width={20}
                src={InfoIcon}
                alt="info"
                className={classes.icon}
              />
              <MyTypography
                variant="body3"
                align={"center"}
                className={classes.textInfo}
              >
                {intl.formatMessage({ defaultMessage: "ไม่พบข้อมูล" })}
              </MyTypography>
            </div>
          </div>
        </TableCell>
      </TableRow>
    ) : (
      <div className={classes.emptyBox}>
        <div className={classes.infoEmpty}>
          <img width={20} src={InfoIcon} alt="info" className={classes.icon} />
          <MyTypography
            variant="body3"
            align={"center"}
            className={classes.textInfo}
          >
            {intl.formatMessage({ defaultMessage: "ไม่พบข้อมูล" })}
          </MyTypography>
        </div>
      </div>
    );

  return (
    <>
      <MyTypography variant="h7" className={classes.titleTable}>
        {intl.formatMessage({ defaultMessage: "รายการที่เกี่ยวข้อง" })}
      </MyTypography>
      <div className={classes.boxContainer}>
        <div className={classes.rowMatName}>
          <MyTypography variant={"h6"} className={classes.matName}>
            {dataStockOnHand?.productCode}
          </MyTypography>
          <MyTypography variant={"body3"} className={classes.summaryAs}>
            {dataStockOnHand?.summaryAsOf
              ? `Summary as of ${dataStockOnHand?.summaryAsOf}`
              : ""}
          </MyTypography>
        </div>
        <div className={classes.rowTotal}>
          <MyTypography variant={"body2"} className={classes.boldText}>
            Total UR:{" "}
            <span className={classes.matName}>
              {dataStockOnHand?.totalUr || 0}
            </span>
          </MyTypography>
          <MyTypography variant={"body2"}>
            Customer Order:{" "}
            <span className={classes.boldText}>
              {dataStockOnHand?.customerOrderQuantity || 0}
            </span>
          </MyTypography>
          <MyTypography variant={"body2"}>
            Consignment:{" "}
            <span className={classes.boldText}>
              {dataStockOnHand?.consignmentQuantity || 0}
            </span>
          </MyTypography>
          <MyTypography variant={"body2"}>
            Free Stock:{" "}
            <span className={classes.boldText}>
              {dataStockOnHand?.freeQuantity || 0}
            </span>
          </MyTypography>
          <MyTypography variant={"body2"}>
            Unit:{" "}
            <span className={classes.boldText}>{dataStockOnHand?.unit}</span>
          </MyTypography>
          <div className={classes.plantSelect}>
            <MyTypography variant={"body2"}>Plant:</MyTypography>
            <SingleSelectField
              id="plan-selector"
              choices={plantFilterChoices}
              onChange={handleChangePlantFilter}
              value={filterPlant}
            />
          </div>
        </div>
        <div className={classes.tabBox}>
          {renderCollection(tabList, tab => (
            <div
              key={tab.code}
              className={classNames(
                activeTab === tab.code ? classes.zMax : classes[tab.index]
              )}
            >
              <button
                id={tab.id}
                data-testid={tab.id}
                onClick={() => onTabClick(tab.code)}
                className={classNames(
                  !tab.first && classes.tabB,
                  classes.tableTab,
                  activeTab === tab.code
                    ? classes.tableTabActive
                    : classes.tableTabInactive
                )}
              >
                <MyTypography
                  variant={"body2"}
                  className={classNames(
                    activeTab === tab.code
                      ? classes.innerTabActive
                      : classes.innerTab
                  )}
                >
                  {tab.title}
                </MyTypography>
              </button>
            </div>
          ))}
        </div>
        {dataStockOnHand ? (
          <>
            <div className={classes.rootTable} ref={containerRef}>
              <TableContainer className={classNames(classes.tableContainer)}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      {activeTab === TableTab.tabA
                        ? renderHeaderTabCustomer()
                        : null}
                      {activeTab === TableTab.tabB
                        ? renderHeaderTabConsiment()
                        : null}
                      {activeTab === TableTab.tabC
                        ? renderHeaderTabFree()
                        : null}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeTab === TableTab.tabA
                      ? renderBodyTabCustomer()
                      : null}
                    {activeTab === TableTab.tabB
                      ? renderBodyTabConsiment()
                      : null}
                    {activeTab === TableTab.tabC ? renderBodyTabFree() : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
            <StickyHorizontalScrollBar
              container={containerRef}
              key={`scroll-${activeTab}`}
              reRender={reRender}
            />
          </>
        ) : (
          renderBoxEmpty()
        )}
      </div>
    </>
  );
};

export default StockOnHandTableResult;
